import React, { useState, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import axios from 'axios';
import {
  Code, Bot, Cpu, Calculator, Languages, ArrowRight, ArrowLeft, Star,
  Users, BookOpen, GraduationCap, ChevronRight, Sparkles, Monitor,
  Zap, Award, Puzzle, Globe, CheckCircle, MessageCircle, Mic, Target,
  Layers, Lightbulb, Gamepad2, Palette, Braces, Database, Brain,
  Cog, Trophy, FileText, Presentation, Home, Search
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

// ═══════════════════════════════════════════════
//  CURRICULUM DATA — Categories → Programs → Topics
// ═══════════════════════════════════════════════

const CATEGORIES = [
  {
    id: 'coding',
    name: 'Coding',
    icon: Code,
    color: '#2563EB',
    bg: '#EFF6FF',
    border: '#BFDBFE',
    desc: 'From block-based coding to full-stack development — learn to build real software step by step.',
    programs: [
      {
        id: 'tech-sprout',
        name: 'Tech Sprout',
        level: 'Beginner',
        age: '6-12',
        desc: 'Spark curiosity and creativity with hands-on tech and playful visual coding.',
        color: '#2563EB',
        recommended: [6, 7, 8, 9, 10, 11, 12],
        topics: [
          { name: 'Scratch Junior', icon: Sparkles, desc: 'Visual coding for young learners using drag-and-drop blocks to tell stories and create games.', outcomes: ['Create animated stories and greeting cards', 'Understand sequencing and basic events', 'Build simple interactive projects independently'] },
          { name: 'Scratch', icon: Puzzle, desc: 'Build interactive games, animations, and stories using Scratch\'s powerful block-based language.', outcomes: ['Build interactive games with scoring systems', 'Understand loops, conditionals, and variables', 'Create multi-scene animations with sound effects'] },
          { name: 'Code.org', icon: Globe, desc: 'Structured coding courses with guided puzzles that teach computational thinking fundamentals.', outcomes: ['Solve 100+ coding puzzles progressively', 'Master pattern recognition and debugging', 'Write first algorithms using visual blocks'] },
          { name: 'Thunkable', icon: Monitor, desc: 'Build real mobile apps with a visual drag-and-drop app builder — see your code come to life on a phone.', outcomes: ['Design and build functional mobile apps', 'Understand UI/UX basics and app navigation', 'Publish a working app to share with friends'] },
        ],
      },
      {
        id: 'tech-titan',
        name: 'Tech Titan',
        level: 'Advanced',
        age: '13-16',
        desc: 'Transform ideas into real-world tech with professional programming languages and frameworks.',
        color: '#7C3AED',
        recommended: [13, 14, 15, 16],
        topics: [
          { name: 'Python', icon: Code, desc: 'The world\'s most popular programming language — from basics to advanced data structures and algorithms.', outcomes: ['Write clean, efficient Python programs', 'Build automation scripts and data analysis tools', 'Master OOP, file handling, and API integration'] },
          { name: 'Java', icon: Braces, desc: 'Enterprise-grade programming language used in Android apps, games, and large-scale systems.', outcomes: ['Build object-oriented Java applications', 'Understand inheritance, polymorphism, and interfaces', 'Create console-based games and utilities'] },
          { name: 'C & C++', icon: Cpu, desc: 'Low-level programming for competitive coding, system software, and performance-critical applications.', outcomes: ['Write efficient C/C++ programs with pointers', 'Solve competitive programming problems', 'Understand memory management and data structures'] },
          { name: 'HTML, CSS & JavaScript', icon: Monitor, desc: 'Build beautiful, interactive websites from scratch — the foundation of all web development.', outcomes: ['Design responsive websites with modern CSS', 'Add interactivity with JavaScript and DOM manipulation', 'Build and deploy a personal portfolio website'] },
          { name: 'Web & App Development', icon: Zap, desc: 'Full-stack development with React, Node.js, and databases — build production-ready applications.', outcomes: ['Build full-stack web applications', 'Work with databases (MongoDB, Firebase)', 'Deploy live projects with authentication and APIs'] },
          { name: 'Game Development', icon: Gamepad2, desc: 'Create 2D and 3D games using industry tools — from concept to playable product.', outcomes: ['Design game mechanics and level systems', 'Build games with Unity or Godot engine', 'Publish games that friends and family can play'] },
        ],
      },
    ],
  },
  {
    id: 'robotics',
    name: 'Robotics',
    icon: Cpu,
    color: '#D97706',
    bg: '#FFFBEB',
    border: '#FDE68A',
    desc: 'Hands-on robotics combining hardware and software — build, program, and control real robots.',
    programs: [
      {
        id: 'robotics-explorer',
        name: 'Robotics Explorer',
        level: 'All Levels',
        age: '8-14',
        desc: 'From basic circuits to autonomous robots — learn engineering, electronics, and programming together.',
        color: '#D97706',
        recommended: [8, 9, 10, 11, 12, 13, 14],
        topics: [
          { name: 'Introduction to Robotics', icon: Cog, desc: 'Understand the fundamentals of robotics — sensors, actuators, and microcontrollers.', outcomes: ['Identify and explain robot components', 'Build a simple robot using a kit', 'Understand input-process-output in robotics'] },
          { name: 'Arduino Programming', icon: Cpu, desc: 'Program Arduino boards to control LEDs, motors, and sensors with C-based code.', outcomes: ['Write Arduino sketches for hardware control', 'Build sensor-based projects (light, temperature)', 'Create an obstacle-avoiding robot'] },
          { name: 'IoT & Smart Devices', icon: Globe, desc: 'Connect robots and devices to the internet — build smart home prototypes and connected systems.', outcomes: ['Build WiFi-connected sensor systems', 'Create a smart home automation prototype', 'Understand cloud data logging and dashboards'] },
        ],
      },
    ],
  },
  {
    id: 'ai',
    name: 'AI',
    icon: Bot,
    color: '#7C3AED',
    bg: '#F5F3FF',
    border: '#C4B5FD',
    desc: 'Master AI, Machine Learning, and Prompt Engineering — the most in-demand skills of the future.',
    programs: [
      {
        id: 'ai-beyond',
        name: 'AI & Beyond',
        level: 'All Levels',
        age: 'All Ages',
        desc: 'From understanding AI concepts to building real ML models and mastering prompt engineering.',
        color: '#7C3AED',
        recommended: [10, 11, 12, 13, 14, 15, 16],
        topics: [
          { name: 'AI Fundamentals', icon: Brain, desc: 'Understand what AI is, how it works, and its real-world applications across industries.', outcomes: ['Explain AI, ML, and Deep Learning concepts', 'Identify AI applications in daily life', 'Understand ethical implications of AI'] },
          { name: 'Prompt Engineering', icon: MessageCircle, desc: 'Master the art of communicating with AI tools — write effective prompts for ChatGPT, Gemini, and more.', outcomes: ['Write precise, effective AI prompts', 'Use AI tools for content creation and analysis', 'Build AI-powered workflows and automations'] },
          { name: 'Machine Learning', icon: Database, desc: 'Train real ML models using Python — from data preprocessing to model deployment.', outcomes: ['Build and train classification models', 'Understand supervised vs unsupervised learning', 'Deploy a working ML model as a web app'] },
          { name: 'AI Projects', icon: Lightbulb, desc: 'Build portfolio-worthy AI projects — chatbots, image classifiers, recommendation systems, and more.', outcomes: ['Build an AI chatbot from scratch', 'Create an image recognition system', 'Present AI projects in a professional portfolio'] },
        ],
      },
    ],
  },
  {
    id: 'mathematics',
    name: 'Mathematics',
    icon: Calculator,
    color: '#059669',
    bg: '#ECFDF5',
    border: '#A7F3D0',
    desc: 'Personalized math coaching for competitions and curriculum — from AMC to MATHCOUNTS and beyond.',
    programs: [
      {
        id: 'math-competition',
        name: 'Competition Math',
        level: 'All Levels',
        age: 'Grade-wise',
        desc: 'Structured preparation for AMC 8/10/12, MATHCOUNTS, Math Kangaroo, and olympiad pathways.',
        color: '#059669',
        recommended: [8, 9, 10, 11, 12, 13, 14, 15, 16],
        topics: [
          { name: 'AMC 8 Preparation', icon: Trophy, desc: 'Targeted coaching for the AMC 8 — 25 multiple-choice problems testing logic, arithmetic, and early algebra.', outcomes: ['Solve AMC 8 level problems confidently', 'Master number theory and counting principles', 'Achieve qualifying scores for further competitions'] },
          { name: 'AMC 10/12 Preparation', icon: Award, desc: 'Advanced problem-solving for AMC 10 and AMC 12 — algebra, geometry, probability, and combinatorics.', outcomes: ['Solve advanced competition math problems', 'Master proof techniques and elegant solutions', 'Qualify for AIME and olympiad pathways'] },
          { name: 'MATHCOUNTS Training', icon: Target, desc: 'Sprint, Target, and Team round preparation for MATHCOUNTS — speed, accuracy, and strategy.', outcomes: ['Excel in Sprint and Target rounds', 'Develop rapid mental math techniques', 'Compete confidently at state and national levels'] },
          { name: 'Math Kangaroo', icon: Globe, desc: 'Fun, accessible international math competition preparation for all grade levels.', outcomes: ['Solve creative mathematical puzzles', 'Build strong problem-solving intuition', 'Earn medals and certificates at international level'] },
        ],
      },
      {
        id: 'math-curriculum',
        name: 'Mathematics Mastery Program',
        level: 'Beginner to Advanced',
        age: '5-16',
        desc: 'Building Problem Solvers, Not Just Formula Learners — an interactive Math program focused on concepts, reasoning, problem solving, and real-world applications. Aligned with USA Common Core learning standards.',
        color: '#0D9488',
        recommended: [5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16],
        topics: [
          { name: 'Math Explorer · Grades K–2', icon: Sparkles, desc: 'Foundations that make math feel intuitive — counting, early operations, shapes, and patterns through play.', outcomes: ['Number Sense', 'Addition & Subtraction', 'Early Multiplication', 'Shapes & Patterns', 'Measurement & Data'] },
          { name: 'Math Innovator · Grades 3–5', icon: Layers, desc: 'Move from procedures to reasoning — fluency with operations, fractions, and the language of data.', outcomes: ['Multiplication & Division', 'Fractions & Decimals', 'Geometry Basics', 'Graphs & Data', 'Word Problems'] },
          { name: 'Math Challenger · Grades 6–8', icon: Puzzle, desc: 'Build the bridge to algebraic thinking — proportional reasoning, integers, and early algebra.', outcomes: ['Ratios & Percentages', 'Integers', 'Algebra Foundations', 'Geometry', 'Statistics & Probability'] },
          { name: 'Math Elite · Grades 9–10', icon: Braces, desc: 'High-school core skills — algebra fluency, functions, proofs, and data-driven problem solving.', outcomes: ['Algebra', 'Functions', 'Geometry Proofs', 'Trigonometry Basics', 'Data Analysis'] },
        ],
        approach: [
          { name: 'Concept Clarity', desc: 'Visual, story-led explanations that lock in the "why" before the "how".' },
          { name: 'Guided Practice', desc: 'Targeted worksheets and live problem-solving sessions with 1:1 feedback.' },
          { name: 'Real-World Applications', desc: 'Every concept anchored to scenarios kids actually meet — sports, money, design, science.' },
          { name: 'Regular Progress Tracking', desc: 'Termly assessments, parent updates, and personalised next-step plans.' },
        ],
      },
    ],
  },
  {
    id: 'ielts',
    name: 'IELTS',
    icon: Languages,
    color: '#BE185D',
    bg: '#FDF2F8',
    border: '#FBCFE8',
    desc: 'Achieve your target IELTS band score with expert 1:1 coaching across all four modules.',
    programs: [
      {
        id: 'ielts-prep',
        name: 'IELTS Preparation',
        level: 'All Levels',
        age: '15+',
        desc: 'Comprehensive 1:1 IELTS coaching — Listening, Reading, Writing, and Speaking modules.',
        color: '#BE185D',
        recommended: [15, 16],
        topics: [
          { name: 'Listening & Reading', icon: BookOpen, desc: 'Strategies and practice for high scores in IELTS Listening and Reading sections.', outcomes: ['Achieve 7+ in Listening and Reading', 'Master skimming, scanning, and inference skills', 'Handle all question types confidently'] },
          { name: 'Writing (Task 1 & 2)', icon: FileText, desc: 'Academic and General Training writing — graphs, essays, and letters with band 7+ techniques.', outcomes: ['Write coherent, well-structured essays', 'Describe graphs and charts effectively', 'Use advanced vocabulary and grammar naturally'] },
          { name: 'Speaking', icon: Mic, desc: 'Fluency, pronunciation, and confidence building for the IELTS Speaking test.', outcomes: ['Speak fluently on any topic for 2+ minutes', 'Use idiomatic expressions naturally', 'Score 7+ with confident delivery'] },
          { name: 'Mock Tests & Review', icon: Target, desc: 'Full-length mock tests under timed conditions with detailed feedback and score analysis.', outcomes: ['Complete full tests within time limits', 'Identify and fix recurring mistakes', 'Achieve target band score consistently'] },
        ],
      },
    ],
  },
  {
    id: 'communication',
    name: 'Spoken Mastery',
    icon: Mic,
    color: '#4F46E5',
    bg: '#EEF2FF',
    border: '#C7D2FE',
    desc: 'Build confidence in public speaking, presentation skills, and effective communication.',
    programs: [
      {
        id: 'comm-skills',
        name: 'Spoken Mastery',
        level: 'All Levels',
        age: 'All Ages',
        desc: 'From stage fright to stage presence — structured program to build confident communicators.',
        color: '#4F46E5',
        recommended: [8, 9, 10, 11, 12, 13, 14, 15, 16],
        topics: [
          { name: 'Public Speaking', icon: Presentation, desc: 'Overcome fear, structure speeches, and deliver compelling presentations to any audience.', outcomes: ['Deliver 5-minute speeches confidently', 'Structure arguments logically', 'Handle audience questions with poise'] },
          { name: 'Debate & Argumentation', icon: MessageCircle, desc: 'Learn formal debate formats, critical thinking, and persuasive argumentation techniques.', outcomes: ['Participate in formal debates confidently', 'Build and counter arguments effectively', 'Think critically under pressure'] },
          { name: 'Storytelling & Creative Writing', icon: Palette, desc: 'Craft compelling narratives — from personal anecdotes to creative fiction and non-fiction.', outcomes: ['Write engaging stories and essays', 'Use narrative techniques for impact', 'Present stories verbally with confidence'] },
          { name: 'Interview & Presentation Skills', icon: Users, desc: 'Prepare for school interviews, MUN, and professional presentations with structured practice.', outcomes: ['Ace school and college interviews', 'Create impactful slide presentations', 'Communicate ideas clearly and concisely'] },
        ],
      },
    ],
  },
];

// ═══════════════════════════════════════════════
//  PROGRAMS PAGE COMPONENT
// ═══════════════════════════════════════════════

const ProgramsPage = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  // Navigation state from URL params
  const categoryId = searchParams.get('category') || null;
  const programId = searchParams.get('program') || null;

  // Filters
  const [ageFilter, setAgeFilter] = useState('');
  const [levelFilter, setLevelFilter] = useState('');

  // Demo booking
  const [demoOpen, setDemoOpen] = useState(false);
  const [demoForm, setDemoForm] = useState({ name: '', email: '', phone: '', course_interest: '', message: '', timezone: 'Asia/Kolkata', preferred_date: '' });
  const [submitting, setSubmitting] = useState(false);

  // Derived data
  const activeCategory = CATEGORIES.find(c => c.id === categoryId);
  const activeProgram = activeCategory?.programs.find(p => p.id === programId);

  // Filter logic for Level 1
  const filteredCategories = CATEGORIES;

  const filteredPrograms = useMemo(() => {
    if (!activeCategory) return [];
    return activeCategory.programs.filter(p => {
      if (levelFilter && levelFilter !== 'all') {
        if (p.level.toLowerCase() !== levelFilter.toLowerCase()) return false;
      }
      if (ageFilter && ageFilter !== 'all') {
        const age = parseInt(ageFilter);
        if (!p.recommended.includes(age)) return false;
      }
      return true;
    });
  }, [activeCategory, ageFilter, levelFilter]);

  const recommendedProgram = useMemo(() => {
    if (!activeCategory || !ageFilter || ageFilter === 'all') return null;
    const age = parseInt(ageFilter);
    const match = activeCategory.programs.find(p => p.recommended.includes(age));
    return match?.id || null;
  }, [activeCategory, ageFilter]);

  const goToCategory = (id) => {
    setSearchParams({ category: id });
    setAgeFilter('');
    setLevelFilter('');
  };
  const goToProgram = (catId, progId) => setSearchParams({ category: catId, program: progId });
  const goBack = () => {
    if (programId) {
      setSearchParams({ category: categoryId });
    } else if (categoryId) {
      setSearchParams({});
    } else {
      navigate('/');
    }
  };

  const handleDemoSubmit = async (e) => {
    e.preventDefault();
    if (!demoForm.name || !demoForm.email || !demoForm.phone) { toast.error('Fill required fields'); return; }
    setSubmitting(true);
    try {
      // Set default preferred_date to today if not set
      const submitData = {
        ...demoForm,
        preferred_date: demoForm.preferred_date || new Date().toISOString().split('T')[0]
      };
      await axios.post(`${API}/api/demo-bookings`, submitData);
      toast.success('Demo booked! We\'ll reach out soon.');
      setDemoOpen(false);
      setDemoForm({ name: '', email: '', phone: '', course_interest: '', message: '', timezone: 'Asia/Kolkata', preferred_date: '' });
    } catch { toast.error('Submission failed'); }
    setSubmitting(false);
  };

  const openDemoFor = (courseName) => {
    setDemoForm(prev => ({ ...prev, course_interest: courseName }));
    setDemoOpen(true);
  };

  // ═══════ BREADCRUMB ═══════
  const Breadcrumb = () => (
    <div className="flex items-center gap-2 text-sm font-semibold mb-6 flex-wrap" data-testid="breadcrumb">
      <button onClick={() => navigate('/')} className="text-[#1E40AF] hover:underline flex items-center gap-1">
        <Home className="w-3.5 h-3.5" /> Home
      </button>
      <ChevronRight className="w-3.5 h-3.5 text-[#94A3B8]" />
      {!categoryId ? (
        <span className="text-[#0F172A] font-bold">Programs</span>
      ) : (
        <button onClick={() => setSearchParams({})} className="text-[#1E40AF] hover:underline">Programs</button>
      )}
      {activeCategory && (
        <>
          <ChevronRight className="w-3.5 h-3.5 text-[#94A3B8]" />
          {!programId ? (
            <span className="text-[#0F172A] font-bold">{activeCategory.name}</span>
          ) : (
            <button onClick={() => setSearchParams({ category: categoryId })} className="text-[#1E40AF] hover:underline">{activeCategory.name}</button>
          )}
        </>
      )}
      {activeProgram && (
        <>
          <ChevronRight className="w-3.5 h-3.5 text-[#94A3B8]" />
          <span className="text-[#0F172A] font-bold">{activeProgram.name}</span>
        </>
      )}
    </div>
  );

  // ═══════ FEATURED PROGRAMS (Dashboard Cards) ═══════
  const FEATURED_PROGRAMS = [
    { id: 'scratch', name: 'Scratch Coding', age: '6-10', level: 'Beginner', desc: 'Build games, stories, and animations through visual coding.', levels: 8, sessions: 24, icon: Puzzle, color: '#2563EB', accentBg: '#EFF6FF', link: { category: 'coding', program: 'tech-sprout' } },
    { id: 'python', name: 'Python Programming', age: '11-16', level: 'Intermediate', desc: 'The world\'s most popular programming language — from basics to advanced projects.', levels: 6, sessions: 18, icon: Code, color: '#7C3AED', accentBg: '#F5F3FF', link: { category: 'coding', program: 'tech-titan' } },
    { id: 'webdev', name: 'Web Development', age: '12-16', level: 'Intermediate', desc: 'Build beautiful, interactive websites from scratch with HTML, CSS & JavaScript.', levels: 5, sessions: 15, icon: Monitor, color: '#0891B2', accentBg: '#ECFEFF', link: { category: 'coding', program: 'tech-titan' } },
    { id: 'robotics', name: 'Robotics & AI', age: '8-14', level: 'Advanced', desc: 'Combine hardware and software — build, program, and control real robots.', levels: 4, sessions: 12, icon: Cpu, color: '#D97706', accentBg: '#FFFBEB', link: { category: 'robotics', program: 'robotics-explorer' } },
    { id: 'appdev', name: 'App Development', age: '13-16', level: 'Advanced', desc: 'Create real mobile and web apps with React, Node.js, and databases.', levels: 5, sessions: 15, icon: Zap, color: '#EA580C', accentBg: '#FFF7ED', link: { category: 'coding', program: 'tech-titan' } },
  ];

  const [activeFilter, setActiveFilter] = useState('All');
  const [searchQuery, setSearchQuery] = useState('');
  const FILTER_TABS = ['All', 'Beginner', 'Intermediate', 'Advanced'];

  const filteredFeatured = FEATURED_PROGRAMS.filter(p => {
    const matchFilter = activeFilter === 'All' || p.level === activeFilter;
    const matchSearch = !searchQuery || p.name.toLowerCase().includes(searchQuery.toLowerCase()) || p.desc.toLowerCase().includes(searchQuery.toLowerCase());
    return matchFilter && matchSearch;
  });

  // ═══════ LEVEL 1: Category Grid ═══════
  const renderCategories = () => (
    <div>
      <div className="text-center mb-10">
        <h1 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-[#0F172A] mb-3" data-testid="programs-heading">
          Explore Our <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#2563EB] via-[#7C3AED] to-[#06B6D4]">Programs</span>
        </h1>
        <p className="text-base sm:text-lg text-[#334155] font-semibold max-w-2xl mx-auto">
          Choose a learning track to discover age-appropriate programs and detailed curriculum
        </p>
      </div>

      {/* ── Featured Program Cards with Filters ── */}
      <div className="mb-14">
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-6">
          {/* Filter Tabs */}
          <div className="flex items-center gap-1.5 bg-white rounded-xl p-1 border border-slate-200 shadow-sm" data-testid="filter-tabs">
            {FILTER_TABS.map(tab => (
              <button key={tab} onClick={() => setActiveFilter(tab)}
                className={`px-4 py-2 text-xs font-extrabold rounded-lg uppercase tracking-wide transition-all duration-200 ${
                  activeFilter === tab ? 'bg-[#1E40AF] text-white shadow-md' : 'text-[#334155] hover:bg-slate-100'
                }`} data-testid={`filter-${tab.toLowerCase()}`}>
                {tab}
              </button>
            ))}
          </div>
          {/* Search */}
          <div className="relative w-full sm:w-64">
            <Input placeholder="Search programs..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)}
              className="pl-9 h-10 rounded-xl bg-white border-slate-200 text-[#0F172A] font-bold placeholder:text-slate-400 shadow-sm"
              data-testid="program-search" />
            <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" /></svg>
          </div>
        </div>

        {/* Program Cards Grid */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5" data-testid="featured-programs-grid">
          {filteredFeatured.map((prog, i) => {
            const Icon = prog.icon;
            return (
              <div key={prog.id} onClick={() => goToProgram(prog.link.category, prog.link.program)}
                className="bg-white rounded-2xl border border-slate-200/80 shadow-sm hover:shadow-xl hover:-translate-y-1.5 hover:border-slate-300 transition-all duration-300 cursor-pointer group overflow-hidden"
                style={{ animationDelay: `${i * 60}ms` }}
                data-testid={`featured-card-${prog.id}`}>
                {/* Accent color strip */}
                <div className="h-1.5 w-full" style={{ backgroundColor: prog.color }} />
                <div className="p-5">
                  {/* Icon */}
                  <div className="w-12 h-12 rounded-xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-300"
                    style={{ backgroundColor: prog.accentBg }}>
                    <Icon className="w-6 h-6" style={{ color: prog.color }} />
                  </div>
                  {/* Name */}
                  <h3 className="text-base font-extrabold text-[#0F172A] mb-1">{prog.name}</h3>
                  {/* Age Group */}
                  <p className="text-xs font-bold mb-3" style={{ color: prog.color }}>Ages {prog.age}</p>
                  {/* Description */}
                  <p className="text-sm text-[#334155] font-medium leading-relaxed mb-4 line-clamp-2">{prog.desc}</p>
                  {/* Stats */}
                  <div className="flex items-center gap-3 mb-4 text-xs font-bold text-[#64748B]">
                    <span className="flex items-center gap-1"><Layers className="w-3.5 h-3.5" /> {prog.levels} Levels</span>
                    <span className="w-1 h-1 rounded-full bg-slate-300" />
                    <span className="flex items-center gap-1"><BookOpen className="w-3.5 h-3.5" /> {prog.sessions} Sessions</span>
                  </div>
                  {/* Learn More */}
                  <button className="w-full py-2.5 rounded-xl text-xs font-extrabold uppercase tracking-wide border-2 transition-all duration-200 flex items-center justify-center gap-1.5 group-hover:text-white group-hover:border-transparent"
                    style={{ borderColor: prog.color + '40', color: prog.color, '--hover-bg': prog.color }}
                    onMouseEnter={e => { e.currentTarget.style.backgroundColor = prog.color; e.currentTarget.style.color = '#fff'; e.currentTarget.style.borderColor = prog.color; }}
                    onMouseLeave={e => { e.currentTarget.style.backgroundColor = 'transparent'; e.currentTarget.style.color = prog.color; e.currentTarget.style.borderColor = prog.color + '40'; }}
                    data-testid={`learn-more-${prog.id}`}>
                    Learn More <ArrowRight className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
        {filteredFeatured.length === 0 && (
          <div className="text-center py-10 text-[#334155] font-bold">No programs match your search or filter.</div>
        )}
      </div>

      {/* ── Category Grid (below featured) ── */}
      <div className="mb-8">
        <h2 className="text-xl sm:text-2xl font-extrabold text-[#0F172A] mb-6 uppercase tracking-wide">Browse by <span className="text-[#1E40AF]">Category</span></h2>
      </div>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 max-w-5xl mx-auto">
        {filteredCategories.map((cat) => {
          const Icon = cat.icon;
          return (
            <div
              key={cat.id}
              onClick={() => goToCategory(cat.id)}
              className="relative bg-white/60 backdrop-blur-[16px] rounded-2xl p-6 border border-white/70 shadow-[0_8px_32px_rgba(31,38,135,0.08)] cursor-pointer hover:shadow-[0_12px_40px_rgba(31,38,135,0.15)] hover:-translate-y-1.5 transition-all duration-300 group"
              style={{ background: 'linear-gradient(135deg, rgba(255,255,255,0.72) 0%, rgba(240,247,255,0.55) 100%)' }}
              data-testid={`category-card-${cat.id}`}
            >
              <div className="w-14 h-14 rounded-2xl flex items-center justify-center mb-4 transition-transform group-hover:scale-110" style={{ backgroundColor: cat.color + '15' }}>
                <Icon className="w-7 h-7" style={{ color: cat.color }} />
              </div>
              <h3 className="text-xl font-extrabold text-[#0F172A] mb-2">{cat.name}</h3>
              <p className="text-sm text-[#334155] leading-relaxed mb-4 font-medium">{cat.desc}</p>
              <div className="flex items-center justify-between">
                <span className="text-xs font-bold text-[#334155] bg-white/70 px-3 py-1 rounded-full border border-slate-200/50">
                  {cat.programs.length} program{cat.programs.length > 1 ? 's' : ''}
                </span>
                <button
                  onClick={(e) => { e.stopPropagation(); goToCategory(cat.id); }}
                  className="w-16 h-16 rounded-full font-extrabold text-[9px] leading-tight text-white flex items-center justify-center text-center transition-all duration-300 hover:scale-110 hover:shadow-[0_0_24px_rgba(37,99,235,0.5)]"
                  style={{ background: 'linear-gradient(135deg, #2563EB, #7C3AED, #06B6D4)', boxShadow: '0 0 12px rgba(37,99,235,0.25)' }}
                  data-testid={`explore-${cat.id}`}
                >
                  Trial<br/>Class!
                </button>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );

  // ═══════ LEVEL 2: Programs within Category ═══════
  const renderPrograms = () => {
    if (!activeCategory) return null;
    const CatIcon = activeCategory.icon;
    return (
      <div>
        {/* Category header */}
        <div className="flex items-center gap-4 mb-6">
          <div className="w-14 h-14 rounded-2xl flex items-center justify-center" style={{ backgroundColor: activeCategory.color + '15' }}>
            <CatIcon className="w-7 h-7" style={{ color: activeCategory.color }} />
          </div>
          <div>
            <h1 className="text-2xl sm:text-3xl font-extrabold text-[#0F172A]" data-testid="category-heading">{activeCategory.name} Programs</h1>
            <p className="text-sm text-[#334155] font-medium">{activeCategory.desc}</p>
          </div>
        </div>

        {/* Filters */}
        {activeCategory.programs.length > 1 && (
          <div className="flex items-center gap-3 mb-8 flex-wrap" data-testid="program-filters">
            <div className="flex items-center gap-2">
              <Label className="text-xs font-bold text-[#334155]">Age:</Label>
              <Select value={ageFilter} onValueChange={setAgeFilter}>
                <SelectTrigger className="w-28 h-8 text-xs bg-white/70 backdrop-blur-sm border-white/50" data-testid="age-filter">
                  <SelectValue placeholder="All ages" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All ages</SelectItem>
                  {[6,7,8,9,10,11,12,13,14,15,16].map(a => (
                    <SelectItem key={a} value={String(a)}>{a} years</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-center gap-2">
              <Label className="text-xs font-bold text-[#334155]">Level:</Label>
              <Select value={levelFilter} onValueChange={setLevelFilter}>
                <SelectTrigger className="w-32 h-8 text-xs bg-white/70 backdrop-blur-sm border-white/50" data-testid="level-filter">
                  <SelectValue placeholder="All levels" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All levels</SelectItem>
                  <SelectItem value="beginner">Beginner</SelectItem>
                  <SelectItem value="advanced">Advanced</SelectItem>
                  <SelectItem value="all levels">All Levels</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        )}

        {/* Program cards */}
        {filteredPrograms.length === 0 ? (
          <div className="bg-white/60 backdrop-blur-[16px] rounded-2xl p-10 text-center border border-white/70 shadow-[0_8px_32px_rgba(31,38,135,0.08)]">
            <p className="text-[#334155] font-bold">No programs match the selected filters. Try adjusting age or level.</p>
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 gap-6 max-w-4xl">
            {filteredPrograms.map((prog) => {
              const isRecommended = recommendedProgram === prog.id;
              return (
                <div
                  key={prog.id}
                  onClick={() => goToProgram(activeCategory.id, prog.id)}
                  className={`relative bg-white/60 backdrop-blur-[16px] rounded-2xl overflow-hidden border-2 cursor-pointer hover:shadow-[0_12px_40px_rgba(31,38,135,0.15)] hover:-translate-y-1.5 transition-all duration-300 ${
                    isRecommended ? 'border-emerald-400 ring-2 ring-emerald-200' : 'border-white/70'
                  }`}
                  style={{ boxShadow: '0 8px 32px rgba(31,38,135,0.08)', background: 'linear-gradient(135deg, rgba(255,255,255,0.72) 0%, rgba(240,247,255,0.55) 100%)' }}
                  data-testid={`program-card-${prog.id}`}
                >
                  {isRecommended && (
                    <div className="absolute top-0 right-0 bg-emerald-500 text-white text-[10px] font-extrabold px-3 py-1 rounded-bl-xl" data-testid="recommended-badge">
                      Recommended for age {ageFilter}
                    </div>
                  )}
                  <div className="p-6">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-xs font-bold uppercase tracking-wide px-2.5 py-0.5 rounded-full" style={{ backgroundColor: prog.color + '15', color: prog.color }}>
                        {prog.level}
                      </span>
                      <span className="text-xs font-bold text-[#334155] bg-white/70 px-2.5 py-0.5 rounded-full border border-slate-200/40">
                        Ages {prog.age}
                      </span>
                    </div>
                    <h3 className="text-xl font-extrabold text-[#0F172A] mt-3 mb-2">{prog.name}</h3>
                    <p className="text-sm text-[#334155] leading-relaxed mb-4 font-medium">{prog.desc}</p>
                    <div className="flex items-center justify-between">
                      <span className="text-xs font-bold text-[#334155]">{prog.topics.length} topics</span>
                      <button
                        onClick={(e) => { e.stopPropagation(); goToProgram(activeCategory.id, prog.id); }}
                        className="w-16 h-16 rounded-full font-extrabold text-[9px] leading-tight text-white flex items-center justify-center text-center transition-all duration-300 hover:scale-110 hover:shadow-[0_0_24px_rgba(37,99,235,0.5)]"
                        style={{ background: 'linear-gradient(135deg, #2563EB, #7C3AED, #06B6D4)', boxShadow: '0 0 12px rgba(37,99,235,0.25)' }}
                        data-testid={`view-curriculum-${prog.id}`}
                      >
                        Trial<br/>Class!
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    );
  };

  // ═══════ LEVEL 3: Curriculum Topics ═══════
  const renderCurriculum = () => {
    if (!activeProgram) return null;
    return (
      <div>
        {/* Program header */}
        <div className="relative bg-white/60 backdrop-blur-[16px] rounded-2xl p-6 border border-white/70 shadow-[0_8px_32px_rgba(31,38,135,0.08)] mb-8" style={{ background: 'linear-gradient(135deg, rgba(255,255,255,0.72) 0%, rgba(240,247,255,0.55) 100%)' }} data-testid="program-header">
          <div className="flex items-center gap-2 mb-2">
            <span className="text-xs font-bold uppercase tracking-wide px-2.5 py-0.5 rounded-full" style={{ backgroundColor: activeProgram.color + '15', color: activeProgram.color }}>
              {activeProgram.level}
            </span>
            <span className="text-xs font-bold text-[#334155] bg-white/70 px-2.5 py-0.5 rounded-full border border-slate-200/40">Ages {activeProgram.age}</span>
          </div>
          <h1 className="text-2xl sm:text-3xl font-extrabold text-[#0F172A] mb-1" data-testid="curriculum-heading">
            {activeProgram.name} <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#2563EB] via-[#7C3AED] to-[#06B6D4]">Curriculum</span>
          </h1>
          <p className="text-sm text-[#334155] font-medium mb-4">{activeProgram.desc}</p>
          <button
            onClick={() => openDemoFor(activeProgram.name)}
            className="w-20 h-20 rounded-full font-extrabold text-[10px] leading-tight text-white flex items-center justify-center text-center transition-all duration-300 hover:scale-110 hover:shadow-[0_0_30px_rgba(37,99,235,0.5)]"
            style={{ background: 'linear-gradient(135deg, #2563EB, #7C3AED, #06B6D4)', boxShadow: '0 0 16px rgba(37,99,235,0.3)' }}
            data-testid="program-book-trial"
          >
            FREE<br/>Trial<br/>Class!
          </button>
        </div>

        {/* Topic cards */}
        <div className="space-y-5">
          {activeProgram.topics.map((topic, i) => {
            const TopicIcon = topic.icon;
            return (
              <div
                key={i}
                className="relative bg-white/60 backdrop-blur-[16px] rounded-2xl border border-white/70 overflow-hidden hover:shadow-[0_12px_40px_rgba(31,38,135,0.15)] transition-all duration-300"
                style={{ boxShadow: '0 8px 32px rgba(31,38,135,0.08)', background: 'linear-gradient(135deg, rgba(255,255,255,0.72) 0%, rgba(240,247,255,0.55) 100%)' }}
                data-testid={`topic-card-${i}`}
              >
                <div className="p-6">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0" style={{ backgroundColor: activeProgram.color + '12' }}>
                      <TopicIcon className="w-6 h-6" style={{ color: activeProgram.color }} />
                    </div>
                    <div className="flex-1">
                      <h3 className="text-lg font-extrabold text-[#0F172A] mb-1">{topic.name}</h3>
                      <p className="text-sm text-[#334155] leading-relaxed font-medium mb-4">{topic.desc}</p>

                      {/* Learning Outcomes */}
                      <div className="bg-white/50 backdrop-blur-sm rounded-xl p-4 border border-white/60">
                        <p className="text-xs font-extrabold text-[#1E40AF] uppercase tracking-wide mb-2.5 flex items-center gap-1.5">
                          <GraduationCap className="w-3.5 h-3.5" /> What the student will achieve
                        </p>
                        <div className="space-y-2">
                          {topic.outcomes.map((o, oi) => (
                            <div key={oi} className="flex items-start gap-2">
                              <CheckCircle className="w-4 h-4 mt-0.5 flex-shrink-0 text-emerald-500" />
                              <span className="text-sm text-[#0F172A] font-medium">{o}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="px-6 pb-5 flex justify-end">
                  <button
                    onClick={() => openDemoFor(topic.name)}
                    className="w-14 h-14 rounded-full font-extrabold text-[8px] leading-tight text-white flex items-center justify-center text-center transition-all duration-300 hover:scale-110 hover:shadow-[0_0_24px_rgba(37,99,235,0.5)]"
                    style={{ background: 'linear-gradient(135deg, #2563EB, #7C3AED, #06B6D4)', boxShadow: '0 0 12px rgba(37,99,235,0.25)' }}
                    data-testid={`book-topic-${i}`}
                  >
                    Trial<br/>Class!
                  </button>
                </div>
              </div>
            );
          })}
        </div>

        {/* Optional: CodeITT Learning Approach (rendered only when the program defines it) */}
        {Array.isArray(activeProgram.approach) && activeProgram.approach.length > 0 && (
          <div
            className="mt-10 bg-white/60 backdrop-blur-[16px] rounded-2xl border border-white/70 p-6"
            style={{ background: 'linear-gradient(135deg, rgba(255,255,255,0.72) 0%, rgba(240,247,255,0.55) 100%)', boxShadow: '0 8px 32px rgba(31,38,135,0.08)' }}
            data-testid="learning-approach-section"
          >
            <div className="flex items-center gap-2 mb-1">
              <Lightbulb className="w-5 h-5" style={{ color: activeProgram.color }} />
              <h3 className="text-lg font-extrabold text-[#0F172A]">CodeITT Learning Approach</h3>
            </div>
            <p className="text-sm text-[#334155] font-medium mb-4">How we teach math at every grade band.</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {activeProgram.approach.map((step, si) => (
                <div
                  key={si}
                  className="bg-white/50 backdrop-blur-sm rounded-xl p-4 border border-white/60"
                  data-testid={`approach-item-${si}`}
                >
                  <div className="flex items-start gap-2">
                    <CheckCircle className="w-4 h-4 mt-0.5 flex-shrink-0 text-emerald-500" />
                    <div>
                      <p className="text-sm font-extrabold text-[#0F172A]">{step.name}</p>
                      {step.desc && <p className="text-xs text-[#334155] font-medium mt-0.5 leading-relaxed">{step.desc}</p>}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  };

  // ═══════ MAIN RENDER ═══════
  return (
    <div className="min-h-screen bg-[#F0F7FF]" style={{ fontFamily: "'Nunito', sans-serif" }} data-testid="programs-page">
      {/* Navbar */}
      <nav className="bg-[#0f1419]/95 backdrop-blur-md border-b border-cyan-500/10 sticky top-0 z-50" data-testid="programs-navbar">
        <div className="px-4 sm:px-8 lg:px-12 h-16 flex items-center justify-between">
          <div className="flex items-center gap-4 cursor-pointer" onClick={() => navigate('/')}>
            <img src="/assets/logo.png" alt="CodeITT" className="h-14 w-auto object-contain rounded-xl" />
          </div>
          <div className="flex items-center gap-3">
            <Button variant="ghost" onClick={() => navigate('/')} className="text-slate-300 hover:text-white font-bold text-sm">
              <Home className="w-4 h-4 mr-1.5" /> Home
            </Button>
            <Button onClick={() => navigate('/login')} className="bg-cyan-500 hover:bg-cyan-400 text-black font-extrabold rounded-full px-6 h-9 text-sm" data-testid="programs-login-btn">
              Login
            </Button>
          </div>
        </div>
      </nav>

      {/* Content */}
      <div className="px-4 sm:px-8 lg:px-16 py-8 max-w-7xl mx-auto">
        <Breadcrumb />

        {/* Back button */}
        {categoryId && (
          <button onClick={goBack} className="flex items-center gap-1.5 text-sm font-bold text-[#1E40AF] hover:text-[#1E3A8A] mb-6 transition-colors" data-testid="back-button">
            <ArrowLeft className="w-4 h-4" /> Back
          </button>
        )}

        {/* Level routing */}
        {!categoryId && renderCategories()}
        {categoryId && !programId && renderPrograms()}
        {categoryId && programId && renderCurriculum()}
      </div>

      {/* Demo Booking Dialog */}
      <Dialog open={demoOpen} onOpenChange={setDemoOpen}>
        <DialogContent className="max-w-md bg-[#0f1419] border-cyan-500/20 rounded-2xl text-white">
          <DialogHeader>
            <DialogTitle className="text-xl font-extrabold text-white">Book a FREE Trial Class</DialogTitle>
            <DialogDescription className="text-slate-400 font-bold">
              {demoForm.course_interest && `Selected: ${demoForm.course_interest}`}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleDemoSubmit} className="space-y-3 mt-2">
            <div><Label className="text-xs font-bold text-slate-300">Child's Name *</Label><Input value={demoForm.name} onChange={e => setDemoForm({...demoForm, name: e.target.value})} placeholder="Name" required className="rounded-lg h-9 bg-white/5 border-white/10 text-white font-bold placeholder:text-slate-500" data-testid="demo-name" /></div>
            <div><Label className="text-xs font-bold text-slate-300">Email *</Label><Input type="email" value={demoForm.email} onChange={e => setDemoForm({...demoForm, email: e.target.value})} placeholder="parent@email.com" required className="rounded-lg h-9 bg-white/5 border-white/10 text-white font-bold placeholder:text-slate-500" data-testid="demo-email" /></div>
            <div><Label className="text-xs font-bold text-slate-300">Phone *</Label><Input value={demoForm.phone} onChange={e => setDemoForm({...demoForm, phone: e.target.value})} placeholder="+91 98765 43210" required className="rounded-lg h-9 bg-white/5 border-white/10 text-white font-bold placeholder:text-slate-500" data-testid="demo-phone" /></div>
            <div><Label className="text-xs font-bold text-slate-300">Message</Label><Textarea value={demoForm.message} onChange={e => setDemoForm({...demoForm, message: e.target.value})} placeholder="Any specific needs..." rows={2} className="rounded-lg bg-white/5 border-white/10 text-white font-bold placeholder:text-slate-500" /></div>
            <Button type="submit" disabled={submitting} className="w-full bg-cyan-500 hover:bg-cyan-400 text-black h-10 rounded-full font-extrabold text-sm uppercase tracking-wide" data-testid="demo-submit">
              {submitting ? 'Submitting...' : 'Book My FREE Trial'}
            </Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ProgramsPage;
